import React from 'react';
import { AdminNote } from '../../types/viewServiceRequestType';
import {
  TECHNICIAN_UPDATE_SR_STATUS_LABELS as T,
  VIEW_SERVICE_REQUEST_LABELS as L,
} from '../../constants/labels';
import {
  formatSubmittedOn,
  getMissedVisitReasonLabel,
} from './viewServiceRequestHelpers';

/** Single timestamped admin note entry rendered inside the notes list. */
function AdminNoteEntry({ note }: { note: AdminNote }) {
  // RR-622: a missed-visit note carries a structured `reason` code; surface it
  // as a labelled line above the (optional) free-text comment.
  const reasonLabel = getMissedVisitReasonLabel(note.reason);
  return (
    <div className="flex flex-col gap-2 w-full py-4">
      {reasonLabel && (
        <p
          className="text-[14px] font-medium leading-5 tracking-[0.25px] text-on-surface-variant"
          data-testid="view-sr-admin-note-reason"
        >
          {T.FIELD_MISSED_VISIT_REASON}: {reasonLabel}
        </p>
      )}
      {note.note && (
        <p className="text-[14px] font-normal leading-5 tracking-[0.25px] text-on-surface-variant wrap-break-word whitespace-pre-wrap">
          {note.note}
        </p>
      )}
      <p className="text-[12px] font-normal leading-4 tracking-[0.4px] text-on-surface-variant">
        {formatSubmittedOn(note.created_at)}
      </p>
    </div>
  );
}

interface SRAdminNotesTabProps {
  adminNotes: AdminNote[] | null;
}

/** "Notatki administratora" tab — lists all admin notes or an empty state. */
const SRAdminNotesTab: React.FC<SRAdminNotesTabProps> = ({ adminNotes }) => {
  const notes = adminNotes ?? [];
  return (
    <div
      className="flex flex-col gap-4 border border-[#DCDDE0] rounded-lg p-5 flex-1"
      data-testid="view-sr-admin-notes-section"
    >
      <p className="text-[18px] font-medium leading-6.5 text-on-surface-variant">
        {L.NAV_ADMIN_NOTES}
      </p>
      {notes.length > 0 ? (
        <div className="flex flex-col">
          {notes.map((n, i) => (
            <AdminNoteEntry key={i} note={n} />
          ))}
        </div>
      ) : (
        <p
          className="px-5 py-2 text-center text-[14px] font-normal leading-5 tracking-[0.25px] text-on-surface-variant"
          data-testid="view-sr-admin-notes-empty"
        >
          {L.EMPTY_ADMIN_NOTES_MESSAGE}
        </p>
      )}
    </div>
  );
};

export default SRAdminNotesTab;
